import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock3, Minus, Plus, RefreshCw, ShoppingBasket, Wallet, Ribbon, ChevronRight } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import UserNavbar from "../../components/UserNavbar";

const API_BASE = "https://www.frendrops.in/api";

type Tab = "toBePlaced" | "tomorrow" | "restOfMonth";
type BasketItem = {
  id?: string; _id?: string; item_id?: string; product_id?: any;
  product_name?: string; name?: string; image?: string; date?: string;
  price?: number; mrp_price?: number; quantity?: number; quantity_added?: number; quantityAdded?: number;
  basket_line_id?: string;
  product_description?: string; is_order_placed?: boolean;
};
type BasketData = {
  orderToBePlaced: BasketItem[]; tomorrowOrders: BasketItem[]; restOrders: BasketItem[];
  delivery_info?: { subtotal?: number; charge?: number; total?: number };
};

const emptyBasket: BasketData = { orderToBePlaced: [], tomorrowOrders: [], restOrders: [] };

const lineId = (item: BasketItem) => String(item.id || item._id || item.basket_line_id || item.item_id || "");
const qty = (item: BasketItem) => Number(item.quantity_added ?? item.quantity ?? item.quantityAdded ?? 0) || 0;
const name = (item: BasketItem) => item.product_name || item.name || "Product";

export default function BasketPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("toBePlaced");
  const [basket, setBasket] = useState<BasketData>(emptyBasket);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [error, setError] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [showMembershipBanner, setShowMembershipBanner] = useState(false);

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const goLogin = useCallback(() => {
    navigate("/login", { state: { from: { pathname: "/basket" } } });
  }, [navigate]);

  const fetchBasket = useCallback(async (refresh = false) => {
    const token = localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") { goLogin(); return; }
    try {
      refresh ? setRefreshing(true) : setLoading(true);
      setError("");
      const { data } = await axios.get(`${API_BASE}/basket`, { headers: headers() });
      const payload = data?.data ?? data ?? {};
      setBasket({
        orderToBePlaced: Array.isArray(payload.orderToBePlaced) ? payload.orderToBePlaced : [],
        tomorrowOrders: Array.isArray(payload.tomorrowOrders) ? payload.tomorrowOrders : [],
        restOrders: Array.isArray(payload.restOrders) ? payload.restOrders : [],
        delivery_info: payload.delivery_info || {},
      });
    } catch (e: any) {
      if (e?.response?.status === 401 || e?.response?.status === 403) { goLogin(); return; }
      setError(e?.response?.data?.message || "Unable to load your basket.");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [goLogin, headers]);

  const fetchExtras = useCallback(async () => {
    try {
      const [mastersRes, membershipRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/masters`, { headers: headers() }),
        axios.get(`${API_BASE}/membership/details`, { headers: headers() }),
      ]);
      if (mastersRes.status === "fulfilled") {
        const m = mastersRes.value.data?.data ?? mastersRes.value.data ?? {};
        setWalletBalance(Number(m.wallet_balance) || 0);
      }
      if (membershipRes.status === "fulfilled") {
        const raw = membershipRes.value.data;
        const d = raw?.data ?? raw ?? {};
        const hasDetails = Boolean(raw && typeof raw === "object");
        const active = Boolean(d.has_active_membership ?? d.hasActiveMembership);
        setShowMembershipBanner(hasDetails && !active);
      }
    } catch { /* basket must remain usable if optional extras fail */ }
  }, [headers]);

  useEffect(() => { fetchBasket(); fetchExtras(); }, [fetchBasket, fetchExtras]);

  const updateQuantity = async (item: BasketItem, next: number) => {
    const id = lineId(item);
    if (!id || item.is_order_placed || next < 0) return;
    try {
      setError("");
      await axios.put(`${API_BASE}/basket/update-quantity/${encodeURIComponent(id)}`, {
        quantity: next, ...(item.date ? { delivery_date: item.date } : {}),
      }, { headers: headers() });
      await Promise.all([fetchBasket(true), fetchExtras()]);
    } catch (e: any) {
      if (e?.response?.status === 401 || e?.response?.status === 403) { goLogin(); return; }
      setError(e?.response?.data?.message || "Unable to update basket.");
    }
  };

  const placeOrder = async () => {
    if (!basket.orderToBePlaced.length || placingOrder) return;
    try {
      setPlacingOrder(true); setError("");
      await axios.post(`${API_BASE}/orders/checkout`, {}, { headers: headers() });
      await fetchBasket(true);
      setActiveTab("tomorrow");
    } catch (e: any) {
      if (e?.response?.status === 401 || e?.response?.status === 403) { goLogin(); return; }
      const d = e?.response?.data;
      if (d?.type === "insufficient_wallet_balance") {
        navigate("/wallet");
        return;
      }
      setError(d?.message || "Unable to place order.");
    } finally { setPlacingOrder(false); }
  };

  const current = activeTab === "toBePlaced" ? basket.orderToBePlaced : basket.tomorrowOrders;
  const groupedRest = useMemo(() => {
    const groups: Record<string, BasketItem[]> = {};
    basket.restOrders.forEach(item => { if (item.date) (groups[item.date] ||= []).push(item); });
    return Object.entries(groups).sort(([a], [b]) => {
      const parse = (v:string) => { const [d,m,y]=v.split("-").map(Number); return new Date(y,m-1,d).getTime(); };
      return parse(a)-parse(b);
    });
  }, [basket.restOrders]);
  const [selectedDate, setSelectedDate] = useState("");
  useEffect(() => {
    if (groupedRest.length && !groupedRest.some(([d]) => d === selectedDate)) setSelectedDate(groupedRest[0][0]);
  }, [groupedRest, selectedDate]);

  const renderItem = (item: BasketItem, placed = false) => {
    const quantity = qty(item);
    return <div key={lineId(item) || `${name(item)}-${item.date}`} className="flex gap-4 rounded-2xl border border-violet-100 bg-white p-4 shadow-sm">
      {item.image ? <img src={item.image} alt={name(item)} className="h-20 w-20 rounded-xl object-cover" /> :
        <div className="h-20 w-20 rounded-xl bg-violet-50 flex items-center justify-center"><ShoppingBasket className="text-violet-300" /></div>}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div><h3 className="font-semibold text-gray-900">{name(item)}</h3>{item.date && <p className="mt-1 text-sm font-medium text-violet-600">Delivery: {item.date}</p>}</div>
          {placed && <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">Order Placed</span>}
        </div>
        <div className="mt-2 flex items-center gap-2">
          {Number(item.mrp_price) > 0 && <span className="text-sm text-gray-400 line-through">₹{Number(item.mrp_price) * quantity}</span>}
          <span className="font-bold text-gray-900">₹{(Number(item.price)||0) * quantity}</span>
        </div>
        {item.product_description && <p className="mt-1 text-sm text-gray-500">{item.product_description}</p>}
        {!placed && <div className="mt-3 flex items-center justify-end">
          <div className="flex items-center overflow-hidden rounded-xl bg-violet-600 text-white">
            <button onClick={() => updateQuantity(item, Math.max(0, quantity - 1))} className="p-2.5"><Minus size={16}/></button>
            <span className="min-w-10 text-center font-semibold">{quantity}</span>
            <button onClick={() => updateQuantity(item, quantity + 1)} className="p-2.5"><Plus size={16}/></button>
          </div>
        </div>}
      </div>
    </div>;
  };

  return <div className="min-h-screen bg-[#f8f7ff]">
    <UserNavbar />
    <main className="mx-auto max-w-5xl px-4 pt-40 pb-28">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3"><CalendarDays className="text-violet-700"/><h1 className="text-2xl font-bold">My Basket</h1></div>
        <button onClick={() => fetchBasket(true)} disabled={refreshing} className="rounded-xl bg-white p-2.5 text-violet-700 shadow-sm"><RefreshCw size={18} className={refreshing?"animate-spin":""}/></button>
      </div>

      <div className="mb-4 overflow-x-auto border-b border-gray-200">
        <div className="flex min-w-max gap-7">
          {([["toBePlaced","Order to be Placed"],["tomorrow","Tomorrow's Order"],["restOfMonth","Rest of the Month"]] as [Tab,string][]).map(([key,label]) =>
            <button key={key} onClick={()=>setActiveTab(key)} className={`pb-3 text-sm font-semibold ${activeTab===key?"border-b-2 border-violet-700 text-violet-700":"text-gray-500"}`}>{label}</button>
          )}
        </div>
      </div>

      <div className="mb-5 flex items-center gap-2 rounded-xl bg-violet-100 px-4 py-3 text-sm"><Clock3 size={16} className="text-violet-700"/>We deliver only <b className="text-violet-800">early morning.</b></div>
      {error && <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {loading ? <div className="py-20 text-center text-gray-500">Loading basket...</div> :
      activeTab === "restOfMonth" ? (
        basket.restOrders.length === 0 ? <Empty title="No Orders for Rest of Month" /> :
        <div>
          <div className="mb-3 rounded-xl bg-violet-100 p-3 text-center text-sm font-semibold text-violet-800">{groupedRest.length} days · {basket.restOrders.length} deliveries</div>
          <p className="mb-2 text-xs text-gray-500">Select a date to update quantities for that delivery.</p>
          <div className="mb-4 flex gap-2 overflow-x-auto pb-2">{groupedRest.map(([date])=><button key={date} onClick={()=>setSelectedDate(date)} className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold ${selectedDate===date?"border-violet-600 bg-violet-100 text-violet-800":"border-gray-200 bg-white"}`}>{date}</button>)}</div>
          <div className="space-y-3">{(groupedRest.find(([d])=>d===selectedDate)?.[1]||[]).map(item=>renderItem(item,false))}</div>
        </div>
      ) : (
        <div>
          <div className="space-y-3">{current.map(item=>renderItem(item, activeTab==="tomorrow"))}</div>
          {!current.length && <Empty title={activeTab==="toBePlaced"?"No items in your basket":"No orders for tomorrow"} subtitle={activeTab==="toBePlaced"?"Add some products to your basket to place an order.":"Your placed orders for tomorrow will appear here."}/>}
          {activeTab === "toBePlaced" && (
            <div className="mt-6">
              {showMembershipBanner && (
                <MembershipBanner onClick={() => navigate("/subscription")} />
              )}
              <div className="rounded-2xl bg-white p-5 shadow-sm">
                <Row label="Sub Total" value={basket.delivery_info?.subtotal || 0} />
                <Row label="Delivery Charges" value={basket.delivery_info?.charge || 0} free />
                <Row label="Total" value={basket.delivery_info?.total || 0} bold />
                <div className="mt-4 rounded-xl border-l-4 border-violet-600 bg-violet-50 p-3 text-xs leading-5 text-gray-600">
                  This is an estimated amount. Your wallet will be charged only after your order is confirmed at the daily order cutoff. The final payable amount may include applicable delivery charges, taxes, discounts, or other adjustments.
                </div>
                {!!basket.orderToBePlaced.length && (
                  <button
                    onClick={placeOrder}
                    disabled={placingOrder}
                    className="mt-4 w-full rounded-xl bg-violet-700 py-3.5 font-bold text-white disabled:opacity-60"
                  >
                    {placingOrder ? "Placing Order..." : "Place Order"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  </div>;
}

function Empty({title,subtitle}:{title:string;subtitle?:string}) {
  return <div className="py-16 text-center"><ShoppingBasket size={52} className="mx-auto text-gray-300"/><h2 className="mt-3 text-lg font-bold">{title}</h2>{subtitle&&<p className="mt-1 text-sm text-gray-500">{subtitle}</p>}</div>;
}
function Row({label,value,bold,free}:{label:string;value:number;bold?:boolean;free?:boolean}) {
  return <div className={`flex justify-between py-2 ${bold?"mt-2 border-t font-bold":""}`}><span>{label}</span><span>{free&&Number(value)===0?"FREE":`₹${Number(value).toFixed(2)}`}</span></div>;
}

function MembershipBanner({onClick}:{onClick:()=>void}) {
  return <button onClick={onClick} className="mb-4 flex w-full gap-3 rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-100"><Ribbon className="text-violet-700" size={22}/></div>
    <div className="flex-1"><div className="font-extrabold text-gray-900">Why Become a Member?</div><div className="mt-1 whitespace-pre-line text-xs leading-5 text-gray-500">• FREE delivery on every order, regardless of cart value.\n• Non-members receive FREE delivery only on orders above ₹500.\n• Exclusive member discounts and cashback.\n• Access to premium offers and additional savings.</div><div className="mt-2 flex items-center gap-1 text-sm font-bold text-violet-700">Explore Membership Benefits <ChevronRight size={16}/></div></div>
  </button>;
}
